import React, { useEffect, useState } from 'react';
import { Grid, InputAdornment } from '@material-ui/core';
import { push } from 'connected-react-router';
import { useDispatch, useSelector } from 'react-redux';

import yup from '../../utility/yup';
import {
  ItemForm,
  ViewFrame,
  FormInput,
  FormSimpleAutoComplete,
  FormUrlInput,
  FieldUserSelect,
  FieldOrganizationSelect,
} from '../../components';
import { useGuard, useTitle } from '../../hooks';
import { Loadable } from '../../store/types';
import { PromotionCode } from '../../types/items';
import SavedSearchService, { SavedSearchPages } from '../../services/SavedSearchService';
import { selectLists } from '../../store/modules/lists/selectors';
import useStyles from './styles';
import { is, roles } from '../../utility/guard';
import { canEditField } from './util';
import Button from '../../components/button/Button';

const promotionCodeSchema = yup.object().shape({
  code: yup.string().min(8).max(12)
    .matches(
      /^.[a-zA-Z0-9]+$/,
      {
        message: 'Only alphanumeric characters',
        excludeEmptyString: true,
      },
    )
    .required()
    .trim()
    .label('Code'),

  price_teleprompter_monthly: yup.number().min(0).required().label('Teleprompter Monthly'),
  price_teleprompter_annual: yup.number().min(0).required().label('Teleprompter Annual'),
  price_pag_monthly: yup.number().min(0).required().label('PAG monthly'),
  price_pag_video: yup.number().min(0).required().label('PAG per Video'),
  price_pro_12: yup.number().min(0).required().label('Pro 12'),
  price_pro_24: yup.number().min(0).required().label('Pro 24'),
  video_units: yup.number().min(0).label('Video credits'),

  discount_percent: yup.number().integer().min(0).max(100).required().label('Discount'),
  duration_periods: yup.number().min(0).max(9999).required().label('Discount months'),
  summary: yup.string().required().label('Summary'),
  landing_page_url: yup.string().url().label('Landing page'),
});

type Props = {
  creatingMode?: boolean;
  record: Loadable<PromotionCode>;
  onSubmit: (data: PromotionCode) => void;
};

const PromotionCodeForm = ({
  creatingMode = false,
  record: { loading, data },
  onSubmit,
}: Props) => {
  const title = data?.code || 'New Promotion Code';
  useTitle(title);
  const classes = useStyles();
  const dispatch = useDispatch();
  const organizationName = data ? data.ref.organization?.common_name : '';
  const userId = data ? (data.user_id ? data.user_id.toString() : '') : '';
  const isAdmin = useGuard(is(roles.admin));
  const isSuperAdmin = useGuard(is(roles.superAdmin));
  const canEditSecureFields = data?.ref.can_edit_secure_fields || false;
  const secureFields = data?.ref.secure_fields || [];
  const [mustEdit, setMustEdit] = useState(false);
  const canEditSecureFiled = canEditField(canEditSecureFields, secureFields, mustEdit);

  useEffect(() => {
    const hotKeyList = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key?.toLocaleLowerCase() === 'l') {
        event.preventDefault();
        dispatch(push('/dashboard/admin/promotion-codes'));
      }
    };
    window.addEventListener('keydown', hotKeyList);

    return () => {
      window.removeEventListener('keydown', hotKeyList);
    };
  }, [dispatch]);

  const lists = useSelector(selectLists);

  const permissionsList = (lists && lists.data) ? lists?.data['user-permissions'].map((item) => ({
    label: item.value,
    id: item.key,
  })) : [];

  const associationsList = (lists && lists.data) ? lists?.data['user-associations'].map((item) => ({
    label: item.value,
    id: item.key,
  })) : [];

  return (
    <ViewFrame
      loading={loading}
      title={title}
      backToListLink
      actionPanel={data && !mustEdit && !loading && !canEditSecureFields && (
        <Button
          onClick={() => setMustEdit(true)}
          variant="contained"
          color="primary"
          className={classes.editButton}
        >
          Edit
        </Button>
      )}
    >
      <ItemForm
        creatingMode={creatingMode}
        initialValues={data}
        validationSchema={promotionCodeSchema}
        onSubmit={onSubmit}
        loading={loading}
        enableReinitialize
        preventCancelChanges
        validateOnMount={false}
        validateOnBlur={false}
        validateOnChange
      >
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Grid item xs={12} lg={6}>
              <FormInput
                label="Code"
                name="code"
                className={classes.uppercaseField}
                autoFocus
                getValueAfterInit={() => SavedSearchService.getValue(SavedSearchPages.PromotionCodes)}
                disabled={!canEditSecureFiled('code')}
              />
            </Grid>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormInput
              label="Teleprompter Monthly ($)"
              name="price_teleprompter_monthly"
              disabled={!canEditSecureFiled('price_teleprompter_monthly')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormInput
              label="Teleprompter Annual ($)"
              name="price_teleprompter_annual"
              disabled={!canEditSecureFiled('price_teleprompter_annual')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormInput
              label="PAG monthly ($)"
              name="price_pag_monthly"
              disabled={!canEditSecureFiled('price_pag_monthly')}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <FormInput
              label="PAG per Video ($)"
              name="price_pag_video"
              disabled={!canEditSecureFiled('price_pag_video')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormInput
              label="Pro 12 ($)"
              name="price_pro_12"
              disabled={!canEditSecureFiled('price_pro_12')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormInput
              label="Pro 24 ($)"
              name="price_pro_24"
              disabled={!canEditSecureFiled('price_pro_24')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormInput
              label="Discount % Other"
              name="discount_percent"
              disabled={!canEditSecureFiled('discount_percent')}
              endAdornment={<InputAdornment position="end">%</InputAdornment>}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormInput
              label="Discount months (zero is no end)"
              name="duration_periods"
              disabled={!canEditSecureFiled('duration_periods')}
              endAdornment={<InputAdornment position="end">months</InputAdornment>}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormInput
              label="Video credits"
              name="video_units"
              disabled={!canEditSecureFiled('video_units')}
            />
          </Grid>
          <Grid item xs={12}>
            <FormInput
              label="Summary"
              name="summary"
              disabled={!canEditSecureFiled('summary')}
            />
          </Grid>
          <Grid item xs={12}>
            <FormUrlInput
              label="Landing page"
              name="landing_page_url"
              disabled={!canEditSecureFiled('landing_page_url')}
            />
          </Grid>
          <Grid item xs={12}>
            <Grid item xs={12} lg={6}>
              <FieldOrganizationSelect
                fieldName="organization_id"
                label="Organization"
                name={organizationName}
                disabled={!canEditSecureFiled('organization_id')}
              />
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid item xs={12} lg={6}>
              <FieldUserSelect
                userId={parseInt(userId, 10)}
                disabled={!canEditSecureFiled('user_id')}
                defaultValue={-1}
              />
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid item xs={12} lg={6}>
              <FormSimpleAutoComplete
                options={associationsList}
                label="Association"
                name="user_association_id"
                disabled={!canEditSecureFiled('user_association_id')}
              />
            </Grid>
          </Grid>


          <Grid item xs={12}>
            <Grid item xs={12} lg={6}>
              <FormSimpleAutoComplete
                options={permissionsList}
                label="Permissions"
                name="user_permissions_id"
                disabled={!canEditSecureFiled('user_permissions_id')}
              />
            </Grid>
          </Grid>
          {(isAdmin || isSuperAdmin)
          && (
            <Grid item xs={12}>
              <Grid item xs={12} lg={6}>
                <FormInput
                  type="textarea"
                  label="Notes"
                  name="notes"
                  disabled={!canEditSecureFiled('notes')}
                />
              </Grid>
            </Grid>
          )}
          <Grid item xs={12}>
            <Grid item xs={12} lg={6}>
              <FormUrlInput
                label="Pricing URL"
                name="ref.pricing_url"
                disabled
              />
            </Grid>
          </Grid>
          {!creatingMode
          && (
            <Grid item xs={12}>
              <Grid item xs={12} lg={6}>
                <FormInput
                  label="Created by"
                  name="ref.creator.full_name_first_last"
                  disabled
                />
              </Grid>
            </Grid>
          )}
        </Grid>
      </ItemForm>
    </ViewFrame>
  );
};

export default PromotionCodeForm;
